import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Label, SpecraButton, SpecraInput, SpecraWordmark } from '@mono/ui-components';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import SSOButton from '../components/SSOButton';
import { toast } from '../components/toast';
import { useAuth } from '../contexts/authContext';
export default function AuthPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const { login, register, isAuthenticated, isLoading, ssoProviders, ssoLoading, fetchSSOProviders } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Determine if we're in register mode based on the path
    const isRegisterMode = location.pathname === '/register';
    // Fetch SSO providers on mount
    useEffect(()=>{
        fetchSSOProviders();
    }, [
        fetchSSOProviders
    ]);
    // Redirect authenticated users to home
    useEffect(()=>{
        if (isAuthenticated && !isLoading) {
            navigate('/');
        }
    }, [
        isAuthenticated,
        isLoading,
        navigate
    ]);
    const handleSubmit = async (e)=>{
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (isRegisterMode) {
                // Registration flow with name field
                if (!name.trim()) {
                    toast({
                        type: 'error',
                        description: 'Name is required'
                    });
                    setIsSubmitting(false);
                    return;
                }
                // Validate password length
                if (password.length < 8) {
                    setPasswordError(true);
                    toast({
                        type: 'error',
                        description: 'Password must be at least 8 characters long'
                    });
                    setIsSubmitting(false);
                    return;
                }
                await register(email, password, name);
                setPasswordError(false);
                toast({
                    type: 'success',
                    description: 'Account created successfully!'
                });
                setIsSuccessful(true);
            } else {
                // Login flow
                await login(email, password);
                setIsSuccessful(true);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : isRegisterMode ? 'Failed to create account. Please try again.' : 'Invalid email or password. Please try again.';
            toast({
                type: 'error',
                description: errorMessage
            });
        } finally{
            setIsSubmitting(false);
        }
    };
    // Show loading while checking authentication
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-dvh w-screen items-center justify-center bg-background",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "text-center",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-4 text-muted-foreground",
                        children: "Loading..."
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Helmet, {
                children: [
                    /*#__PURE__*/ _jsxs("title", {
                        children: [
                            isRegisterMode ? 'Sign Up' : 'Sign In',
                            " - GovCon AI Dashboard"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("meta", {
                        name: "description",
                        content: isRegisterMode ? 'Create your GovCon AI Dashboard account' : 'Sign in to GovCon AI Dashboard'
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex h-dvh w-screen items-start pt-12 md:pt-0 md:items-center justify-center bg-background",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "w-full max-w-md overflow-hidden rounded-2xl flex flex-col gap-8",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col items-center justify-center gap-2 px-4 text-center sm:px-16",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraWordmark, {
                                    height: 28,
                                    className: "mb-4"
                                }),
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-xl font-semibold dark:text-zinc-50",
                                    children: isRegisterMode ? 'Sign Up' : 'Sign In'
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-gray-500 dark:text-zinc-400",
                                    children: isRegisterMode ? 'Create an account with your email and password' : 'Use your email and password to sign in'
                                })
                            ]
                        }),
                        !ssoLoading && ssoProviders && /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col gap-3 px-4 sm:px-16",
                            children: [
                                ssoProviders.providers.google && /*#__PURE__*/ _jsx(SSOButton, {
                                    provider: "google",
                                    url: ssoProviders.urls.google_login,
                                    disabled: isSubmitting
                                }),
                                ssoProviders.providers.microsoft && /*#__PURE__*/ _jsx(SSOButton, {
                                    provider: "microsoft",
                                    url: ssoProviders.urls.microsoft_login,
                                    disabled: isSubmitting
                                }),
                                ssoProviders.providers.google_workspace && /*#__PURE__*/ _jsx(SSOButton, {
                                    provider: "google_workspace",
                                    url: ssoProviders.urls.google_workspace_login,
                                    disabled: isSubmitting
                                }),
                                ssoProviders.providers.microsoft_azure && /*#__PURE__*/ _jsx(SSOButton, {
                                    provider: "microsoft_azure",
                                    url: ssoProviders.urls.microsoft_azure_login,
                                    disabled: isSubmitting
                                })
                            ]
                        }),
                        !ssoLoading && ssoProviders && /*#__PURE__*/ _jsxs("div", {
                            className: "relative px-4 sm:px-16",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "absolute inset-0 flex items-center",
                                    children: /*#__PURE__*/ _jsx("span", {
                                        className: "w-full border-t border-gray-300 dark:border-zinc-700"
                                    })
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "relative flex justify-center text-xs uppercase",
                                    children: /*#__PURE__*/ _jsx("span", {
                                        className: "bg-background px-2 text-gray-500 dark:text-zinc-400",
                                        children: "Or continue with"
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("form", {
                            onSubmit: handleSubmit,
                            className: "flex flex-col gap-4 px-4 sm:px-16",
                            children: [
                                isRegisterMode && /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: "name",
                                            className: "text-zinc-600 font-normal dark:text-zinc-400",
                                            children: "Full Name"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraInput, {
                                            id: "name",
                                            name: "name",
                                            className: "bg-muted text-base md:text-sm",
                                            type: "text",
                                            placeholder: "John Doe",
                                            value: name,
                                            onChange: (e)=>setName(e.target.value),
                                            required: true,
                                            autoFocus: true
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: "email",
                                            className: "text-zinc-600 font-normal dark:text-zinc-400",
                                            children: "Email Address"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraInput, {
                                            id: "email",
                                            name: "email",
                                            className: "bg-muted text-base md:text-sm",
                                            type: "email",
                                            placeholder: "user@acme.com",
                                            autoComplete: "email",
                                            value: email,
                                            onChange: (e)=>setEmail(e.target.value),
                                            required: true,
                                            autoFocus: !isRegisterMode
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: "password",
                                            className: "text-zinc-600 font-normal dark:text-zinc-400",
                                            children: "Password"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "relative",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraInput, {
                                                    id: "password",
                                                    name: "password",
                                                    className: `bg-muted text-base md:text-sm pr-10 ${passwordError ? 'border-red-500 dark:border-red-500' : ''}`,
                                                    type: showPassword ? 'text' : 'password',
                                                    value: password,
                                                    onChange: (e)=>{
                                                        setPassword(e.target.value);
                                                        if (passwordError) setPasswordError(false);
                                                    },
                                                    required: true
                                                }),
                                                /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    onClick: ()=>setShowPassword(!showPassword),
                                                    className: "absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors",
                                                    "aria-label": showPassword ? 'Hide password' : 'Show password',
                                                    children: showPassword ? /*#__PURE__*/ _jsx(IconEyeOff, {
                                                        size: 20,
                                                        stroke: 1.5
                                                    }) : /*#__PURE__*/ _jsx(IconEye, {
                                                        size: 20,
                                                        stroke: 1.5
                                                    })
                                                })
                                            ]
                                        }),
                                        isRegisterMode && /*#__PURE__*/ _jsx("p", {
                                            className: `text-xs ${passwordError ? 'text-red-500' : 'text-gray-500 dark:text-zinc-400'}`,
                                            children: "Must be at least 8 characters long"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "submit",
                                    isLoading: isSuccessful || isSubmitting,
                                    className: "w-full",
                                    children: isRegisterMode ? 'Sign up' : 'Sign in'
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-center text-sm text-gray-600 mt-4 dark:text-zinc-400",
                                    children: [
                                        isRegisterMode ? 'Already have an account? ' : "Don't have an account? ",
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: ()=>{
                                                setIsSuccessful(false);
                                                setIsSubmitting(false);
                                                setEmail('');
                                                setPassword('');
                                                setName('');
                                                setPasswordError(false);
                                                navigate(isRegisterMode ? '/login' : '/register');
                                            },
                                            className: "font-semibold text-gray-800 hover:underline dark:text-zinc-200 cursor-pointer bg-transparent border-0 p-0",
                                            children: isRegisterMode ? 'Sign in' : 'Sign up'
                                        }),
                                        isRegisterMode ? ' instead.' : ' for free.'
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
